import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Star, BadgeCheck, ArrowLeft, Calendar, Clock, Video, Gift, Heart,
  Check, ChevronRight, Search, CalendarCheck, PlayCircle, Play, Sparkles, Quote,
  CreditCard, Landmark, Wallet, Bitcoin, Lock, Copy, X, ChevronDown, ChevronUp,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { translations } from "../i18n/translations";
import { getDetail, SESSION_DURATIONS } from "../i18n/detailContent";
import { EXPERTS, TIME_SLOTS, SITE_SETTINGS, PAYMENT_METHODS, durationPrice, startingPrice, slugify } from "../mock/mock";
import { mediaUrl } from "../lib/media";
import { api, ensureData } from "../lib/api";
import Header from "../components/public/Header";
import { Footer } from "../components/public/Footer";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "../components/ui/accordion";
import { Button } from "../components/ui/button";
import { Toaster } from "../components/ui/toaster";
import { useToast } from "../hooks/use-toast";

const loadExperts = () => JSON.parse(localStorage.getItem("intro_experts") || "null") || EXPERTS;
const nextDays = (n) => Array.from({ length: n }, (_, i) => { const d = new Date(); d.setDate(d.getDate() + i + 1); return d; });

const ytEmbed = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : null;
};
const Stars = ({ n = 5, className = "w-4 h-4" }) => (
  <div className="flex text-amber-400">{Array.from({ length: n }).map((_, i) => <Star key={i} className={`${className} fill-amber-400`} />)}</div>
);

const PAY_ICONS = { card: CreditCard, transfer: Landmark, paypal: Wallet, crypto: Bitcoin, demo: Sparkles };

const BookingCard = ({ expert, d, lang, settings }) => {
  const { toast } = useToast();
  const { loc } = useLang();
  const [expanded, setExpanded] = useState(false);
  const [step, setStep] = useState("select"); // select | pay
  const [dur, setDur] = useState(expert.duration || 30);
  const [date, setDate] = useState(null);
  const [time, setTime] = useState(null);
  const [method, setMethod] = useState(null);
  const [account, setAccount] = useState(null);
  const [copied, setCopied] = useState(null);
  const [plan, setPlan] = useState(null);
  const [expPlan, setExpPlan] = useState(null);
  const [giftOpen, setGiftOpen] = useState(false);
  const [giftEmail, setGiftEmail] = useState("");
  const [giftMsg, setGiftMsg] = useState("");
  const days = nextDays(6);
  const cfg = settings && Object.keys(settings).length ? settings : SITE_SETTINGS;
  const enabledPayments = PAYMENT_METHODS.filter((p) => (cfg.payments || SITE_SETTINGS.payments)[p.id]);
  const bankAccounts = cfg.bankAccounts || SITE_SETTINGS.bankAccounts || [];
  const cardConfig = cfg.cardConfig || SITE_SETTINGS.cardConfig || {};

  const copyIban = (acc) => {
    try { navigator.clipboard.writeText(acc.iban.replace(/\s/g, "")); } catch (e) {}
    setCopied(acc.id);
    setTimeout(() => setCopied(null), 1800);
  };

  const price = durationPrice(expert, dur);
  const startPrice = startingPrice(expert);
  const plans = expert.plans || [];
  const periodLabel = (p) => (p === "session" ? d.perSession : d.perMonth);
  const payAmount = plan ? Number(plan.price) : price;

  const goToPayment = () => {
    setPlan(null);
    setMethod(enabledPayments[0]?.id || null);
    setStep("pay");
  };

  const goToPlan = (p) => {
    if (p.soldOut) return;
    setPlan(p);
    setAccount(null);
    setMethod(enabledPayments[0]?.id || null);
    setStep("pay");
  };

  const confirm = () => {
    api.addBooking({
      expert: expert.name,
      plan: plan ? loc(plan.title) : null,
      period: plan?.period || null,
      date: plan ? null : date,
      time: plan ? null : time,
      dur: plan ? null : dur,
      price: payAmount,
      method,
      account: account?.bank || null,
    }).catch(() => {});
    toast({ title: "Randevu talebin alındı! ✓" });
    setExpanded(false); setStep("select"); setPlan(null); setDate(null); setTime(null); setMethod(null); setAccount(null);
  };

  return (
    <div className="bg-white rounded-3xl border border-neutral-200 shadow-xl shadow-neutral-200/40 overflow-hidden">
      <div className="p-6">
        <div className="flex items-center gap-2 text-indigo-600 font-semibold text-sm">
          <Video className="w-4 h-4" /> {d.consultation}
        </div>
        <p className="mt-2 text-sm text-neutral-500 leading-relaxed">{d.consultationDesc}</p>
        <div className="mt-4 flex items-end justify-between">
          <div>
            <div className="text-xs text-neutral-400">{d.startingAt}</div>
            <div className="font-display font-extrabold text-3xl text-neutral-900">${startPrice}</div>
          </div>
          <div className="text-end">
            <div className="flex items-center gap-1 justify-end"><Stars /><span className="text-sm font-bold text-neutral-800 ms-1">{expert.rating.toFixed(1)}</span></div>
            <div className="text-xs text-neutral-400 mt-0.5">({expert.reviews}) {d.reviewsLabel}</div>
          </div>
        </div>

        <div className="mt-4 flex items-center gap-2 text-sm text-emerald-600 bg-emerald-50 rounded-xl px-3 py-2">
          <Clock className="w-4 h-4" /> {d.nextAvailable} — 12:00, {days[1].getDate()}/{days[1].getMonth() + 1}
        </div>

        {step === "pay" ? (
          <div className="mt-4 animate-fade-in">
            <button onClick={() => { setStep("select"); setPlan(null); }} className="flex items-center gap-1 text-sm text-neutral-500 hover:text-neutral-900 mb-3">
              <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {d.backStep}
            </button>
            {plan && (
              <div className="mb-3 rounded-2xl bg-neutral-50 border border-neutral-200 p-3">
                <div className="text-[11px] uppercase tracking-wide font-bold text-neutral-400">{d.orSelectPlan}</div>
                <div className="font-semibold text-sm text-neutral-900 mt-0.5">{loc(plan.title)}</div>
                <div className="text-sm font-bold text-indigo-600 mt-0.5">${plan.price} {periodLabel(plan.period)}</div>
              </div>
            )}
            <div className="text-sm font-semibold text-neutral-800 mb-2">{d.choosePayment}</div>
            {enabledPayments.length === 0 ? (
              <p className="text-sm text-neutral-400 py-4">{d.noPayment}</p>
            ) : (
              <div className="space-y-2">
                {enabledPayments.map((p) => {
                  const Icon = PAY_ICONS[p.id] || CreditCard;
                  const active = method === p.id;
                  return (
                    <button key={p.id} onClick={() => { setMethod(p.id); setAccount(null); }} className={`w-full flex items-center gap-3 px-3.5 py-3 rounded-2xl border text-start transition ${active ? "border-indigo-600 bg-indigo-50" : "border-neutral-200 hover:border-neutral-400"}`}>
                      <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${active ? "bg-indigo-600" : "bg-neutral-100"}`}>
                        <Icon className={`w-4 h-4 ${active ? "text-white" : "text-neutral-600"}`} />
                      </span>
                      <span className="flex-1 font-medium text-sm text-neutral-800">{loc(p.label)}</span>
                      {active && <Check className="w-4 h-4 text-indigo-600" />}
                    </button>
                  );
                })}
              </div>
            )}

            {method === "card" && (
              <div className="mt-3 space-y-2 animate-fade-in">
                <input placeholder={d.cardNumber} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500" />
                <input placeholder={d.cardHolder} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500" />
                <div className="grid grid-cols-2 gap-2">
                  <input placeholder={d.expiry} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500" />
                  <input placeholder={d.cvc} className="bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500" />
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-neutral-400 pt-1">
                  <Lock className="w-3 h-3" /> {cardConfig.provider === "stripe" ? "Stripe ile güvenli ödeme" : "Güvenli ödeme"}
                </div>
              </div>
            )}
            {method === "transfer" && (
              <div className="mt-3 animate-fade-in">
                <div className="text-xs font-semibold text-neutral-500 mb-2">{d.chooseAccount}</div>
                {bankAccounts.length === 0 ? (
                  <p className="text-sm text-neutral-400 py-2">{d.noAccounts}</p>
                ) : (
                  <div className="space-y-2">
                    {bankAccounts.map((acc) => {
                      const active = account?.id === acc.id;
                      return (
                        <div key={acc.id} onClick={() => setAccount(acc)} className={`rounded-2xl border p-3 cursor-pointer transition ${active ? "border-indigo-600 bg-indigo-50" : "border-neutral-200 hover:border-neutral-400"}`}>
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <Landmark className="w-4 h-4 text-neutral-500" />
                              <span className="font-semibold text-sm text-neutral-900">{acc.bank}</span>
                              <span className="text-[10px] font-bold px-1.5 py-0.5 rounded-full bg-neutral-200 text-neutral-600">{acc.currency}</span>
                            </div>
                            {active && <Check className="w-4 h-4 text-indigo-600" />}
                          </div>
                          <div className="mt-1.5 text-[11px] text-neutral-500">{d.accountHolder}: {acc.holder}</div>
                          <div className="mt-1 flex items-center justify-between gap-2">
                            <code className="text-xs text-neutral-800 font-mono truncate">{acc.iban}</code>
                            <button onClick={(e) => { e.stopPropagation(); copyIban(acc); }} className="shrink-0 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                              {copied === acc.id ? <><Check className="w-3.5 h-3.5" />{d.copied}</> : <><Copy className="w-3.5 h-3.5" />{d.copy}</>}
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
                <p className="mt-2 text-xs text-neutral-400 bg-neutral-50 rounded-xl p-2.5">{d.transferInfo}</p>
              </div>
            )}
            {method === "crypto" && <p className="mt-3 text-xs text-neutral-500 bg-neutral-50 rounded-xl p-3">{d.cryptoInfo}</p>}

            <Button onClick={confirm} disabled={!method || (method === "transfer" && !account)} className="w-full mt-4 h-12 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold disabled:opacity-40">
              <Lock className="w-4 h-4 me-1.5" /> {d.payConfirm} · ${payAmount}
            </Button>
            <p className="mt-2 text-center text-xs text-neutral-400 flex items-center justify-center gap-1"><Lock className="w-3 h-3" /> {d.securePay}</p>
          </div>
        ) : (
          <>
            {!expanded ? (
              <Button onClick={() => setExpanded(true)} className="w-full mt-4 h-12 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold text-base">
                {d.seeTimes}
              </Button>
            ) : (
              <div className="mt-4 animate-fade-in">
                <div className="text-sm font-semibold text-neutral-800 mb-2">{d.selectDuration}</div>
                <div className="grid grid-cols-3 gap-2 mb-4">
                  {SESSION_DURATIONS.map((m) => (
                    <button key={m} onClick={() => setDur(m)} className={`py-2 rounded-xl border text-center transition ${dur === m ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:border-neutral-400"}`}>
                      <div className="text-sm font-medium">{m} {translations[lang]?.expert?.duration}</div>
                      <div className={`text-xs mt-0.5 font-semibold ${dur === m ? "text-white/80" : "text-indigo-600"}`}>${durationPrice(expert, m)}</div>
                    </button>
                  ))}
                </div>
                <div className="text-sm font-semibold text-neutral-800 mb-2 flex items-center gap-1.5"><Calendar className="w-4 h-4" />{d.selectDate}</div>
                <div className="flex gap-2 overflow-x-auto no-scrollbar pb-1 mb-4">
                  {days.map((dd) => {
                    const active = date === dd.toDateString();
                    return (
                      <button key={dd.toDateString()} onClick={() => setDate(dd.toDateString())} className={`shrink-0 w-14 py-2 rounded-xl border text-center transition ${active ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:border-neutral-400"}`}>
                        <div className="text-[10px] uppercase opacity-70">{dd.toLocaleDateString(lang === "ar" ? "ar" : lang, { weekday: "short" })}</div>
                        <div className="font-bold text-lg leading-none mt-0.5">{dd.getDate()}</div>
                      </button>
                    );
                  })}
                </div>
                <div className="text-sm font-semibold text-neutral-800 mb-2 flex items-center gap-1.5"><Clock className="w-4 h-4" />{d.selectTime}</div>
                <div className="grid grid-cols-4 gap-2 mb-4">
                  {TIME_SLOTS.map((s) => (
                    <button key={s} onClick={() => setTime(s)} className={`py-2 rounded-xl border text-sm font-medium transition ${time === s ? "bg-neutral-900 text-white border-neutral-900" : "bg-white border-neutral-200 hover:border-neutral-400 text-neutral-700"}`}>{s}</button>
                  ))}
                </div>
                <Button onClick={goToPayment} disabled={!date || !time} className="w-full h-12 rounded-2xl bg-indigo-600 hover:bg-indigo-500 text-white font-semibold disabled:opacity-40">
                  {date && time && <Check className="w-4 h-4 me-1" />} {d.confirmBooking} · ${price}
                </Button>
              </div>
            )}

            {plans.length > 0 && (
              <div className="mt-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="flex-1 h-px bg-neutral-200" />
                  <span className="text-xs font-semibold text-neutral-400 uppercase tracking-wide">{d.orSelectPlan}</span>
                  <div className="flex-1 h-px bg-neutral-200" />
                </div>
                <div className="space-y-3">
                  {plans.map((pl) => {
                    const items = (loc(pl.included) || "").split("\n").map((s) => s.trim()).filter(Boolean);
                    const open = expPlan === pl.id;
                    const shown = open ? items : items.slice(0, 2);
                    return (
                      <div key={pl.id} className={`relative rounded-2xl border p-4 transition ${pl.soldOut ? "border-neutral-200 bg-neutral-50 opacity-80" : pl.popular ? "border-indigo-500 ring-2 ring-indigo-500/25 bg-indigo-50/40 hover:border-indigo-600" : "border-neutral-200 hover:border-neutral-400"}`}>
                        {pl.popular && !pl.soldOut && (
                          <span className="absolute -top-2.5 end-4 inline-flex items-center gap-1 text-[10px] font-bold px-2.5 py-1 rounded-full bg-indigo-600 text-white shadow-sm">
                            <Sparkles className="w-3 h-3" /> {d.mostPopular}
                          </span>
                        )}
                        {pl.soldOut && <span className="inline-block text-[10px] font-bold px-2 py-0.5 rounded-full bg-neutral-800 text-white mb-2">{d.soldOut}</span>}
                        <div className="font-display font-bold text-neutral-900 leading-snug">{loc(pl.title)}</div>
                        {items.length > 0 && (
                          <>
                            <div className="mt-3 text-xs font-semibold text-neutral-500">{d.whatsIncluded}</div>
                            <ul className="mt-1.5 space-y-1.5">
                              {shown.map((it, i) => (
                                <li key={i} className="flex items-start gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{it}</li>
                              ))}
                            </ul>
                            {items.length > 2 && (
                              <button onClick={() => setExpPlan(open ? null : pl.id)} className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700">
                                {open ? <>{d.seeLess}<ChevronUp className="w-3.5 h-3.5" /></> : <>{d.seeMore}<ChevronDown className="w-3.5 h-3.5" /></>}
                              </button>
                            )}
                          </>
                        )}
                        <div className="mt-3 flex items-end justify-between gap-2">
                          <div className="font-display font-extrabold text-xl text-neutral-900">${pl.price}<span className="text-sm font-semibold text-neutral-400 ms-1">{periodLabel(pl.period)}</span></div>
                          <div className="flex items-center gap-1"><Stars className="w-3.5 h-3.5" /><span className="text-xs font-bold text-neutral-700">{expert.rating.toFixed(1)}</span></div>
                        </div>
                        <Button onClick={() => goToPlan(pl)} disabled={pl.soldOut} className={`w-full mt-3 h-11 rounded-2xl font-semibold ${pl.soldOut ? "bg-neutral-200 text-neutral-400 hover:bg-neutral-200" : "bg-indigo-600 hover:bg-indigo-500 text-white"}`}>
                          {pl.soldOut ? d.soldOut : d.selectPlan}
                        </Button>
                        {pl.footnote && loc(pl.footnote) && <p className="mt-2 text-center text-xs text-indigo-600 font-medium">{loc(pl.footnote)}</p>}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Gift */}
      <button type="button" onClick={() => setGiftOpen(true)} data-testid="gift-card-btn" className="w-full text-start border-t border-neutral-100 p-6 bg-neutral-50 hover:bg-neutral-100 transition-colors">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-2xl bg-rose-100 flex items-center justify-center shrink-0"><Gift className="w-5 h-5 text-rose-500" /></div>
          <div>
            <div className="font-semibold text-neutral-900 text-sm">{d.giftTitle}</div>
            <div className="text-xs text-neutral-500">{d.giftDesc}</div>
          </div>
        </div>
      </button>

      {giftOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setGiftOpen(false)} />
          <div className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl animate-scale-in p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Gift className="w-5 h-5 text-rose-500" /><h3 className="font-display font-bold text-lg">{d.giftTitle}</h3></div>
              <button onClick={() => setGiftOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">{d.giftDesc}</p>
            <input type="email" value={giftEmail} onChange={(e) => setGiftEmail(e.target.value)} placeholder={d.giftEmail} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 mb-2" />
            <textarea rows={3} value={giftMsg} onChange={(e) => setGiftMsg(e.target.value)} placeholder={d.giftMessage} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 mb-3" />
            <Button onClick={() => { if (!giftEmail.trim()) return; api.addBooking({ expert: expert.name, plan: `🎁 ${d.giftTitle}`, period: "gift", price: startPrice, method: "gift", account: giftEmail }).catch(() => {}); toast({ title: d.giftSent }); setGiftOpen(false); setGiftEmail(""); setGiftMsg(""); }} disabled={!giftEmail.trim()} className="w-full h-12 rounded-2xl bg-rose-500 hover:bg-rose-600 text-white font-semibold disabled:opacity-40">
              <Gift className="w-4 h-4 me-1.5" /> {d.giftCta}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

const ExpertDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { lang, loc } = useLang();
  const { toast } = useToast();
  const d = getDetail(lang);
  const [fav, setFav] = useState(false);
  const [videoOpen, setVideoOpen] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [timeOpen, setTimeOpen] = useState(false);
  const [timeEmail, setTimeEmail] = useState("");
  const [timeMsg, setTimeMsg] = useState("");
  const [experts, setExperts] = useState(loadExperts);
  const [settings, setSettings] = useState(SITE_SETTINGS);

  useEffect(() => {
    ensureData(EXPERTS, SITE_SETTINGS)
      .then(({ experts, settings }) => {
        if (experts?.length) setExperts(experts);
        if (settings && Object.keys(settings).length) setSettings(settings);
      })
      .catch(() => {});
  }, []);

  const expert = experts.find((e) => e.id === id || slugify(e.name) === id) || experts[0];
  const catLabel = translations[lang]?.categories?.[expert.category] || expert.category;
  const stepIcons = [Search, CalendarCheck, Video];
  const gallery = ((expert.photos && expert.photos.length ? expert.photos : [expert.photo]) || []).filter(Boolean);

  useEffect(() => {
    setImgIdx(0);
    if (gallery.length > 1) {
      const t = setInterval(() => setImgIdx((i) => (i + 1) % gallery.length), 3500);
      return () => clearInterval(t);
    }
  }, [expert?.id, gallery.length]);

  useEffect(() => {
    if (expert && expert.name) {
      document.title = `${expert.name} — intro`;
      const slug = slugify(expert.name);
      if (id !== slug) navigate(`/expert/${slug}`, { replace: true });
    }
  }, [expert?.id, expert?.name, id, navigate]);

  return (
    <div className="bg-white min-h-screen">
      <Header />
      <div className="pt-24 sm:pt-28 pb-16">
        <div className="max-w-6xl mx-auto px-5 sm:px-8">
          <button onClick={() => navigate("/")} className="inline-flex items-center gap-1.5 text-sm font-medium text-neutral-500 hover:text-neutral-900 mb-6">
            <ArrowLeft className="w-4 h-4 rtl:rotate-180" /> {d.backToExperts}
          </button>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* LEFT */}
            <div className="lg:col-span-2">
              {/* Hero */}
              <div className="flex flex-col sm:flex-row gap-6">
                <div className="relative w-full sm:w-56 shrink-0">
                  <div className="aspect-square rounded-3xl overflow-hidden relative">
                    {gallery.map((src, i) => (
                      <img key={i} src={mediaUrl(src)} alt={expert.name} className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${i === imgIdx ? "opacity-100" : "opacity-0"}`} />
                    ))}
                    {gallery.length > 1 && (
                      <div className="absolute bottom-3 inset-x-0 flex justify-center gap-1.5 z-10">
                        {gallery.map((_, i) => (
                          <button key={i} onClick={() => setImgIdx(i)} className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-5 bg-white" : "w-1.5 bg-white/60"}`} />
                        ))}
                      </div>
                    )}
                  </div>
                  {ytEmbed(expert.videoUrl) && (
                    <button data-testid="watch-intro-btn" onClick={() => setVideoOpen(true)} className="absolute bottom-3 start-3 inline-flex items-center gap-2 rounded-full bg-black/60 hover:bg-black/75 backdrop-blur text-white text-xs font-semibold ps-2 pe-3.5 py-1.5 shadow-lg transition-all hover:scale-105 z-10">
                      <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center shrink-0"><Play className="w-3 h-3 text-neutral-900 fill-neutral-900 ms-0.5" /></span>
                      {d.watchIntro}
                    </button>
                  )}
                  <button onClick={() => setFav((f) => !f)} className="absolute top-3 end-3 w-10 h-10 rounded-full bg-white/90 backdrop-blur flex items-center justify-center shadow-md z-10">
                    <Heart className={`w-5 h-5 ${fav ? "fill-rose-500 text-rose-500" : "text-neutral-600"}`} />
                  </button>
                </div>
                <div className="flex-1">
                  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold ${expert.tier === "gold" ? "bg-amber-100 text-amber-700" : "bg-sky-100 text-sky-700"}`}>
                    <Sparkles className="w-3 h-3" /> {d.topExpert}
                  </span>
                  <div className="flex items-center gap-2 mt-2">
                    <h1 className="font-display font-extrabold text-3xl sm:text-4xl text-neutral-900">{expert.name}</h1>
                    <BadgeCheck className={`w-6 h-6 ${expert.tier === "gold" ? "text-amber-400" : "text-sky-500"}`} />
                  </div>
                  <p className="mt-1.5 text-neutral-500">{loc(expert.title)}</p>
                  <div className="mt-3 flex items-center gap-2">
                    <Stars className="w-5 h-5" />
                    <span className="font-bold text-neutral-900">{expert.rating.toFixed(1)}</span>
                    <span className="text-neutral-400 text-sm">({expert.reviews})</span>
                  </div>
                  <div className="mt-4 inline-flex items-center gap-2 text-sm text-neutral-600 bg-neutral-50 border border-neutral-100 rounded-xl px-3 py-2">
                    <Heart className="w-4 h-4 text-rose-500" /> {d.charityNote} <span className="font-semibold text-neutral-800">{d.charity}</span>
                  </div>
                </div>
              </div>

              {/* Mobile booking */}
              <div className="lg:hidden mt-8"><BookingCard expert={expert} d={d} lang={lang} settings={settings} /></div>

              {/* About */}
              <section className="mt-10">
                <h2 className="font-display font-bold text-2xl text-neutral-900 mb-3">{d.aboutMe}</h2>
                <p className="text-neutral-600 leading-relaxed">{loc(expert.bio)}</p>
                <p className="mt-4 font-semibold text-neutral-800">{d.adviseOn}</p>
                <ul className="mt-3 grid sm:grid-cols-2 gap-2">
                  {expert.expertise.map((x) => (
                    <li key={x} className="flex items-center gap-2 text-neutral-600"><Check className="w-4 h-4 text-emerald-500 shrink-0" /> {x}</li>
                  ))}
                </ul>
              </section>

              {/* What to expect + example questions */}
              <section className="mt-10 grid sm:grid-cols-2 gap-6">
                <div className="rounded-3xl border border-neutral-100 p-6 bg-neutral-50">
                  <div className="flex items-center gap-2 mb-3"><Clock className="w-5 h-5 text-indigo-600" /><h3 className="font-display font-bold text-lg text-neutral-900">{d.whatToExpect}</h3></div>
                  <p className="text-xs font-semibold text-neutral-400 mb-3">{expert.duration} {d.minSession}</p>
                  <ul className="space-y-2.5">
                    {d.whatToExpectItems.map((it) => (<li key={it} className="flex items-start gap-2 text-sm text-neutral-600"><Check className="w-4 h-4 text-emerald-500 mt-0.5 shrink-0" />{it}</li>))}
                  </ul>
                </div>
                <div className="rounded-3xl border border-neutral-100 p-6">
                  <div className="flex items-center gap-2 mb-3"><Quote className="w-5 h-5 text-indigo-600" /><h3 className="font-display font-bold text-lg text-neutral-900">{d.exampleQuestions}</h3></div>
                  <ul className="space-y-2.5">
                    {d.exampleQuestionItems.map((it) => (<li key={it} className="flex items-start gap-2 text-sm text-neutral-600"><ChevronRight className="w-4 h-4 text-indigo-400 mt-0.5 shrink-0 rtl:rotate-180" />{it}</li>))}
                  </ul>
                </div>
              </section>

              {/* Reviews */}
              <section className="mt-10">
                <div className="flex items-center gap-2 mb-1"><h2 className="font-display font-bold text-2xl text-neutral-900">{d.whyValuable}</h2></div>
                <div className="flex items-center gap-2 mb-5"><Stars className="w-5 h-5" /><span className="font-bold text-neutral-900">{expert.rating.toFixed(1)}</span><span className="text-neutral-400 text-sm">({expert.reviews})</span></div>
                <div className="grid sm:grid-cols-2 gap-4">
                  {d.reviews.map((r, i) => (
                    <div key={i} className="rounded-3xl border border-neutral-100 p-5 card-hover">
                      <Stars className="w-4 h-4" />
                      <p className="mt-3 text-neutral-600 text-sm leading-relaxed">"{r.text}"</p>
                      <div className="mt-4 flex items-center gap-2">
                        <div className="w-9 h-9 rounded-full bg-neutral-900 text-white flex items-center justify-center font-bold text-sm">{r.name[0]}</div>
                        <div><div className="font-semibold text-neutral-900 text-sm">{r.name}</div><div className="text-xs text-neutral-400">{r.date}</div></div>
                      </div>
                    </div>
                  ))}
                </div>
              </section>

              {/* How it works */}
              <section className="mt-12">
                <h2 className="font-display font-bold text-2xl text-neutral-900 mb-6">{d.howItWorks}</h2>
                <div className="grid sm:grid-cols-3 gap-5">
                  {d.steps.map((s, i) => {
                    const Icon = stepIcons[i];
                    return (
                      <div key={i} className="rounded-3xl bg-neutral-50 border border-neutral-100 p-5">
                        <div className="w-11 h-11 rounded-2xl bg-neutral-900 flex items-center justify-center mb-3"><Icon className="w-5 h-5 text-white" /></div>
                        <h3 className="font-display font-bold text-neutral-900">{s.t}</h3>
                        <p className="mt-1 text-sm text-neutral-500">{s.d}</p>
                      </div>
                    );
                  })}
                </div>
              </section>

              {/* FAQ */}
              <section className="mt-12">
                <h2 className="font-display font-bold text-2xl text-neutral-900 mb-4">{d.commonQuestions}</h2>
                <Accordion type="single" collapsible className="rounded-3xl border border-neutral-100 divide-y divide-neutral-100 overflow-hidden">
                  {d.faqs.map((f, i) => (
                    <AccordionItem key={i} value={`item-${i}`} className="border-0 px-5">
                      <AccordionTrigger className="text-start font-semibold text-neutral-900 hover:no-underline py-4">{f.q}</AccordionTrigger>
                      <AccordionContent className="text-neutral-500 leading-relaxed pb-4">{f.a}</AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              </section>
            </div>

            {/* RIGHT sticky */}
            <div className="hidden lg:block">
              <div className="sticky top-28">
                <BookingCard expert={expert} d={d} lang={lang} settings={settings} />
                <button onClick={() => setTimeOpen(true)} data-testid="request-time-btn" className="w-full mt-3 text-center text-sm text-neutral-500 hover:text-neutral-900">{d.requestTime}</button>
              </div>
            </div>
          </div>
        </div>
      </div>
      <Footer />

      {videoOpen && ytEmbed(expert.videoUrl) && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/80 backdrop-blur-sm animate-fade-in" onClick={() => setVideoOpen(false)} />
          <div className="relative w-full max-w-3xl animate-scale-in">
            <button onClick={() => setVideoOpen(false)} className="absolute -top-11 end-0 w-9 h-9 flex items-center justify-center rounded-full bg-white/90 hover:bg-white shadow-md">
              <X className="w-5 h-5" />
            </button>
            <div className="rounded-3xl overflow-hidden shadow-2xl bg-black" style={{ aspectRatio: "16/9" }}>
              <iframe src={ytEmbed(expert.videoUrl)} title={`${expert.name} intro`} className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
            </div>
          </div>
        </div>
      )}

      {timeOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm animate-fade-in" onClick={() => setTimeOpen(false)} />
          <div className="relative bg-white rounded-3xl w-full max-w-md shadow-2xl animate-scale-in p-6">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center gap-2"><Clock className="w-5 h-5 text-indigo-600" /><h3 className="font-display font-bold text-lg">{d.requestTimeTitle}</h3></div>
              <button onClick={() => setTimeOpen(false)} className="w-8 h-8 flex items-center justify-center rounded-full hover:bg-neutral-100"><X className="w-5 h-5" /></button>
            </div>
            <p className="text-sm text-neutral-500 mb-4">{d.requestTimeDesc}</p>
            <input type="email" value={timeEmail} onChange={(e) => setTimeEmail(e.target.value)} placeholder={d.giftEmail} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 mb-2" />
            <textarea rows={3} value={timeMsg} onChange={(e) => setTimeMsg(e.target.value)} placeholder={d.requestTimePlaceholder} className="w-full bg-neutral-50 border border-neutral-200 rounded-xl px-3.5 py-2.5 text-sm outline-none focus:border-indigo-500 mb-3" />
            <Button onClick={() => { if (!timeEmail.trim()) return; api.addBooking({ expert: expert.name, plan: `⏰ ${d.requestTimeTitle}`, period: "request", price: 0, method: "time-request", account: `${timeEmail} — ${timeMsg}` }).catch(() => {}); toast({ title: d.requestTimeSent }); setTimeOpen(false); setTimeEmail(""); setTimeMsg(""); }} disabled={!timeEmail.trim()} className="w-full h-12 rounded-2xl bg-neutral-900 hover:bg-neutral-800 text-white font-semibold disabled:opacity-40">
              {d.requestTimeCta}
            </Button>
          </div>
        </div>
      )}

      {/* Mobile sticky bar */}
      <div className="lg:hidden fixed bottom-0 inset-x-0 z-30 bg-white/90 backdrop-blur border-t border-neutral-200 px-5 py-3 flex items-center justify-between">
        <div><div className="font-display font-extrabold text-lg text-neutral-900">${startingPrice(expert)}</div><div className="flex items-center gap-1 text-xs text-neutral-500"><Star className="w-3 h-3 fill-amber-400 text-amber-400" />{expert.rating.toFixed(1)} ({expert.reviews})</div></div>
        <Button onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })} className="rounded-full bg-neutral-900 hover:bg-neutral-800 text-white font-semibold px-6 h-11">{d.seeTimes}</Button>
      </div>
      <Toaster />
    </div>
  );
};

export default ExpertDetail;
